import {
  ENTITY_CLASS,
  type QueryOptions,
  type SensorKey,
  type SensorQuery,
  type SensorQueryFilters,
  streamError,
} from "@packages/core";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { Temporal } from "temporal-polyfill";

import { fetchData, findSensors } from "./functions";
import { QueryKey } from "./query";
import { NETWORK_SEMAPHORE } from "./semaphore";
import { TANSTACK_QUERY_CLIENT } from "./tanstack";

export async function findSensor(sensorKey: SensorKey | false | null | undefined) {
  if (!sensorKey) {
    return undefined;
  }
  const { output } = await fetchData({ type: "Entity::Sensor", input: { sensorKey } });
  if (output instanceof Error) {
    throw output;
  } else {
    return output;
  }
}

function SensorQueryKey(sensorKey: SensorKey | false | null | undefined) {
  return QueryKey(ENTITY_CLASS.SENSOR, sensorKey);
}

export function updateSensorQueryData(
  sensorKey: SensorKey | false | null | undefined,
  newSensor: ReturnType<typeof useSensorQuery>["data"] | false,
) {
  if (sensorKey && newSensor !== false && newSensor !== undefined) {
    TANSTACK_QUERY_CLIENT.setQueryData(
      SensorQueryKey(sensorKey),
      function (
        oldSensor: ReturnType<typeof useSensorQuery>["data"],
      ): ReturnType<typeof useSensorQuery>["data"] {
        return !oldSensor ||
          !newSensor ||
          (Temporal.PlainDateTime.compare(oldSensor.createdAt, newSensor.createdAt) ||
            Temporal.PlainDateTime.compare(oldSensor.updatedAt, newSensor.updatedAt)) < 0
          ? newSensor
          : oldSensor;
      },
    );
  }
}

/** @deprecated 代わりに`useSensorQuery`を使用する。 */
export function useSensor(
  sensorKey: SensorKey | undefined,
  options?: {
    /** 仮想化された一覧で表示される場合に`true`を設定するフラグ */
    virtualized?: boolean;
  },
) {
  const { data: sensor } = useQuery({
    queryKey: SensorQueryKey(sensorKey),
    async queryFn() {
      return findSensor(sensorKey);
    },

    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnMount: !options?.virtualized,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: 60 * 1000,
  });

  return sensor;
}

export function useSensorQuery(sensorKey: SensorKey | false | null | undefined) {
  return useQuery({
    queryKey: SensorQueryKey(sensorKey),
    async queryFn() {
      return findSensor(sensorKey);
    },

    enabled: Boolean(sensorKey),
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: /** 4時間 */ 14_400_000,
  });
}

export async function listSensors(filters?: SensorQueryFilters, options?: QueryOptions) {
  const { output } = await fetchData({ type: "Entities::Sensors", input: { filters, options } });
  if (output instanceof Error) {
    throw output;
  } else {
    return output;
  }
}

function createSensorsQueryKey(...[queries, options]: ReadonlyArray<any>) {
  return QueryKey(ENTITY_CLASS.SENSOR + "List", queries, options);
}

export function removeSensorsCache() {
  TANSTACK_QUERY_CLIENT.invalidateQueries({ queryKey: createSensorsQueryKey().slice(0, 1) });
}

/** @deprecated 代わりに`useSensorInfiniteQuery`を使う。 */
export function useListedSensors(
  filters?: SensorQueryFilters,
  options?: QueryOptions & {
    /** 処理を実行する場合に`true`を設定するフラグ */
    enabled?: boolean;
  },
) {
  const { data, error, fetchNextPage, hasNextPage, isLoading } = useInfiniteQuery({
    queryKey: createSensorsQueryKey(filters, options),
    async queryFn(context) {
      const { nextPageToken, sensors } = await listSensors(filters, {
        ...options,
        nextPageToken: context.pageParam.nextPageToken,
      });

      return { nextPageToken, sensors };
    },

    getNextPageParam(lastPage) {
      return lastPage.nextPageToken ? { nextPageToken: lastPage.nextPageToken } : undefined;
    },
    initialPageParam: {} as { nextPageToken?: Record<string, any> },

    enabled: options?.enabled,
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: 60 * 1000,
  });

  useEffect(
    function () {
      if (error) {
        streamError(error);
      }
    },
    [error],
  );

  return useMemo(
    function () {
      return {
        error,
        fetchNextPage,
        loaded: !hasNextPage,
        loading: isLoading,
        pages: data?.pages ?? [],
      };
    },
    [data?.pages, error, fetchNextPage, hasNextPage, isLoading],
  );
}

function SensorInfiniteQueryKey(...parameters: Parameters<typeof useSensorInfiniteQuery>) {
  return QueryKey(ENTITY_CLASS.SENSOR + "List", ...parameters);
}

export function removeSensorInfiniteQueryCache() {
  TANSTACK_QUERY_CLIENT.invalidateQueries({ queryKey: QueryKey(ENTITY_CLASS.SENSOR + "List") });
}

export function useSensorInfiniteQuery(
  query: SensorQuery,
  options?: Partial<{
    /** 処理を実行する場合に`true`を設定するフラグ */
    enabled: boolean;
  }>,
) {
  return useInfiniteQuery({
    queryKey: SensorInfiniteQueryKey(query, options),
    async queryFn(context) {
      const release = await NETWORK_SEMAPHORE.acquire();
      try {
        const { sensors, numberOfErrors, nextPageToken } = await findSensors({
          ...query,
          ...context.pageParam,
        });

        for (const sensor of sensors) {
          updateSensorQueryData(sensor.sensorKey, sensor);
        }

        return { sensors, numberOfErrors, nextPageToken };
      } finally {
        release();
      }
    },

    getNextPageParam(lastPage) {
      return lastPage.nextPageToken ? { nextPageToken: lastPage.nextPageToken } : undefined;
    },
    initialPageParam: {} as { nextPageToken?: Record<string, any> },

    enabled: options?.enabled,
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: /** 4時間 */ 14_400_000,
  });
}
